/**
 * Terminology utility for switching between apartment and house condo terminology
 *
 * Apartment condos (vertical): Blocos / Unidades
 * House condos (horizontal): Quadras / Lotes
 */

export type CondoType = 'apartamento' | 'casa' | 'misto' | null | undefined;

export interface TerminologyLabels {
  block: {
    singular: string;
    plural: string;
  };
  unit: {
    singular: string;
    plural: string;
  };
}

/**
 * Get the appropriate terminology based on condo type
 * @param condoType - The type of condominium ('apartamento' or 'casa')
 * @returns Object with singular and plural labels for blocks and units
 */
export function getTerminology(condoType: CondoType): TerminologyLabels {
  if (condoType === 'casa') {
    return {
      block: {
        singular: 'Quadra',
        plural: 'Quadras'
      },
      unit: {
        singular: 'Lote',
        plural: 'Lotes'
      }
    };
  }

  // Default to apartment terminology
  return {
    block: {
      singular: 'Bloco',
      plural: 'Blocos'
    },
    unit: {
      singular: 'Unidade',
      plural: 'Unidades'
    }
  };
}

/**
 * The gender of the condo's own words.
 *
 * Forms used to guess it from the last letter — and "Lote" ends in an 'e', so every loteamento
 * read "Nova Lote" and "Número da Lote" on its create form. Gender belongs to the word, so it is
 * stated here once and the forms ask instead of guessing.
 */
export interface WordArticles {
  definite: 'o' | 'a';
  indefinite: 'um' | 'uma';
  ordinal: 'Novo' | 'Nova';
}

const MASCULINE: WordArticles = { definite: 'o', indefinite: 'um', ordinal: 'Novo' };
const FEMININE: WordArticles = { definite: 'a', indefinite: 'uma', ordinal: 'Nova' };

/** Lote (casa/loteamento) is masculine; Unidade (prédio) is feminine. */
export function getUnitArticle(condoType: CondoType): WordArticles {
  return condoType === 'casa' ? MASCULINE : FEMININE;
}

/** Quadra (casa/loteamento) is feminine; Bloco (prédio) is masculine. */
export function getBlockArticle(condoType: CondoType): WordArticles {
  return condoType === 'casa' ? FEMININE : MASCULINE;
}

/**
 * Get block label (singular or plural)
 */
export function getBlockLabel(condoType: CondoType, plural: boolean = false): string {
  const terminology = getTerminology(condoType);
  return plural ? terminology.block.plural : terminology.block.singular;
}

/**
 * Get unit label (singular or plural)
 */
export function getUnitLabel(condoType: CondoType, plural: boolean = false): string {
  const terminology = getTerminology(condoType);
  return plural ? terminology.unit.plural : terminology.unit.singular;
}

export interface UnitLabelInfo {
  number?: string | null;
  blockName?: string | null;
  streetName?: string | null;
  streetNumber?: string | null;
}

/**
 * Formats a unit's location as a single display string, preferring street
 * addressing (Rua X, Número Y) when present, falling back to the condo's
 * block/unit terminology (Quadra/Lote or Bloco/Unidade) otherwise.
 * e.g. "Rua das Flores, 45 – Lote 18", "Quadra 23 – Lote 18", "Bloco A – Unidade 101"
 */
export function formatUnitLabel(info: UnitLabelInfo, condoType?: CondoType): string {
  const parts: string[] = [];
  if (info.streetName) {
    parts.push(info.streetNumber ? `${info.streetName}, ${info.streetNumber}` : info.streetName);
  } else if (info.blockName) {
    parts.push(`${getBlockLabel(condoType)} ${info.blockName}`);
  }
  if (info.number) parts.push(`${getUnitLabel(condoType)} ${info.number}`);
  return parts.join(' – ');
}

// ==================================================================
// PERSON TYPE / ROLE TYPE TRANSLATIONS
// ==================================================================

export type PersonTypeEnglish = 'resident' | 'visitor' | 'service_provider' | 'staff' | 'delivery' | 'condo_employee';
export type PersonTypePortuguese = 'morador' | 'visitante' | 'prestador' | 'funcionario' | 'entregador' | 'funcionario_condominio';

const personTypeToPortuguese: Record<PersonTypeEnglish, string> = {
  resident: 'Morador',
  visitor: 'Visitante',
  service_provider: 'Prestador',
  staff: 'Funcionário',
  delivery: 'Entregador',
  condo_employee: 'Funcionário',
};

const personTypeToEnglish: Record<PersonTypePortuguese, PersonTypeEnglish> = {
  morador: 'resident',
  visitante: 'visitor',
  prestador: 'service_provider',
  funcionario: 'staff',
  entregador: 'delivery',
  funcionario_condominio: 'condo_employee',
};

/**
 * Translates English person type to Portuguese for display
 */
export function translatePersonTypeToPortuguese(personType: string): string {
  return personTypeToPortuguese[personType as PersonTypeEnglish] || personType;
}

/**
 * Translates Portuguese person type to English for API calls
 */
export function translatePersonTypeToEnglish(personType: string): PersonTypeEnglish {
  return personTypeToEnglish[personType as PersonTypePortuguese] || personType as PersonTypeEnglish;
}

/**
 * Normalizes person type to English format (handles both formats)
 */
export function normalizePersonType(personType: string): PersonTypeEnglish {
  if (personType in personTypeToPortuguese) {
    return personType as PersonTypeEnglish;
  }
  return translatePersonTypeToEnglish(personType);
}

/**
 * Gets Tailwind color classes for person type badges
 */
export function getPersonTypeColorClasses(personType: string): string {
  const normalized = normalizePersonType(personType);
  const colorMap: Record<PersonTypeEnglish, string> = {
    resident: 'bg-indigo-100 text-indigo-800',
    visitor: 'bg-green-100 text-green-800',
    service_provider: 'bg-blue-100 text-blue-800',
    staff: 'bg-blue-100 text-blue-800',
    delivery: 'bg-orange-100 text-orange-800',
    condo_employee: 'bg-blue-100 text-blue-800',
  };
  return colorMap[normalized] || 'bg-gray-100 text-gray-800';
}

// ==================================================================
// DEVICE TYPE TRANSLATIONS
// ==================================================================

// Canonical IoTDevice.device_type values (models/identity.go DeviceType* consts) plus
// additional values seen in practice (manufacturer-specific / legacy device rows whose
// device_type was never restricted to the Go enum).
const DEVICE_TYPE_LABELS: Record<string, string> = {
  facial_recognition: 'Reconhecimento Facial',
  facial_reader: 'Leitor Facial',
  rfid_reader: 'Leitor RFID',
  qr_scanner: 'Leitor QR Code',
  // Biometria. Rows in the field carry a bare `fingerprint`; condo-manager writes
  // `fingerprint_scanner` (device_auth_handler.go, device_sync_handler.go). Unmapped, both came out
  // of the humanize fallback in English, on a Portuguese screen, beside "Leitor Facial".
  fingerprint: 'Leitor Biométrico',
  fingerprint_scanner: 'Leitor Biométrico',
  biometric: 'Leitor Biométrico',
  biometric_reader: 'Leitor Biométrico',
  camera: 'Câmera',
  controller: 'Controlador',
  sensor: 'Sensor',
  guarita: 'Guarita',
  alphadigi_lpr: 'Câmera LPR Alphadigi',
  hikvision_lpr: 'Câmera LPR Hikvision',
  lpr: 'Câmera LPR',
  hikvision_face: 'Hikvision Facial',
  controlid_rfid: 'ControlID RFID',
  controlid_face: 'ControlID Facial',
  access_controller: 'Controlador de Acesso',
  controlid: 'ControlID',
  hikvision: 'Hikvision',
  door_controller: 'Controlador de Porta',
  intercom: 'Interfone',
  gate_controller: 'Controlador de Portão',
};

/**
 * Translates a device_type value to a Portuguese display label. Falls back to a
 * humanized version of the raw value (snake_case → Title Case) for anything not
 * explicitly mapped, rather than showing the raw backend value.
 */
export function deviceTypeLabel(deviceType: string | null | undefined): string {
  if (!deviceType) return 'Desconhecido';
  if (DEVICE_TYPE_LABELS[deviceType]) return DEVICE_TYPE_LABELS[deviceType];
  return deviceType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// ==================================================================
// TAG TYPE TRANSLATIONS
// ==================================================================

// Canonical PersonTag.tag_type values (models/person_tag.go TagType* consts).
const TAG_TYPE_LABELS: Record<string, string> = {
  passiva: 'Tag Passiva',
  ativa: 'Tag Ativa',
  controle_remoto: 'Controle Remoto',
  outro: 'Outro',
};

/**
 * Translates a PersonTag.tag_type value to a Portuguese display label. Falls back
 * to a humanized version of the raw value for anything not explicitly mapped.
 */
export function tagTypeLabel(tagType: string | null | undefined): string {
  if (!tagType) return '';
  if (TAG_TYPE_LABELS[tagType]) return TAG_TYPE_LABELS[tagType];
  return tagType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
